import { useMemo } from "react";
import { AppAside } from "@/app-shell";
import { AiScriptExplanationChatView } from "@/payroll/ai-script/ai-script-explanation-chat";
import { useAiScriptExplanationChatSession } from "@/payroll/ai-script/ai-script-explanation-chat/use-ai-script-explanation-chat-session";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { chatSurface } from "@/profitability/job-costing-run/chat-surface";
import type { RecordObject } from "@/profitability/records/objects";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { reportChatPath } from "@/profitability/utils";
import { useReviewLayout } from "./use-review-layout";

/**
 * Volca's chat about one period's report, in the page's aside. A question
 * asked from the page arrives as `question` and is sent as the panel opens.
 * Its words come from what the report declares: its records, its headline
 * figures, its rate and its flags.
 */
type VolcaChatAsideProps = {
  businessId: string;
  runId: string;
  open: boolean;
  question: string | null;
  onQuestionPrepared: () => void;
  onClose: () => void;
  objects: RecordObject[];
  figures: string[];
  selection?: AiScriptChatSelectionContext | null;
  onClearSelection?: () => void;
};

export const VolcaChatAside = (props: VolcaChatAsideProps) =>
  props.open ? <OpenVolcaChatAside {...props} /> : null;

const OpenVolcaChatAside = ({
  businessId,
  runId,
  open,
  question,
  onQuestionPrepared,
  onClose,
  objects,
  figures,
  selection = null,
  onClearSelection,
}: VolcaChatAsideProps) => {
  const { dockChat } = useReviewLayout(open);
  const spec = useReviewSpec();
  const surface = useMemo(
    () => chatSurface({ objects, figures, spec }),
    [objects, figures, spec],
  );
  const session = useAiScriptExplanationChatSession({
    businessId,
    runId,
    buildChatPath: reportChatPath,
    selectionContext: selection,
    pendingQuestion: question,
    sendPendingQuestion: true,
    onQuestionPrepared,
  });
  return (
    <AppAside
      open={open}
      label="Ask Volca"
      overlay={!dockChat}
      onClose={onClose}
    >
      <AiScriptExplanationChatView
        session={session}
        onClearSelectionContext={onClearSelection}
        title={surface.title}
        subtitle={surface.subtitle}
        emptyTitle={surface.emptyTitle}
        emptyDescription={surface.emptyDescription}
        composerPlaceholder={surface.composerPlaceholder}
        suggestedPrompts={surface.suggestedPrompts}
        acceptsImages
        onClose={onClose}
      />
    </AppAside>
  );
};
